import React from "react";
import { useParams } from "react-router-dom";
import {
  MessageBar,
  MessageBarType,
  ProgressIndicator,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
} from "@fluentui/react";
import type { IColumn } from "@fluentui/react";
import PageHeader from "@/ui-controls/PageHeader";
import useTelemetryDetail from "@/components/telemetry/detail/useTelemetryDetail";
import MethodBadge from "@/components/telemetry/common/MethodBadge";
import StatusCodeBadge from "@/components/telemetry/common/StatusCodeBadge";
import HeadersTable from "@/components/telemetry/detail/HeadersTable";
import BodyViewer from "@/components/telemetry/detail/BodyViewer";
import { displayValue } from "@/components/telemetry/common/displayValue";
import styles from "@/components/telemetry/detail/TelemetryDetail.module.css";

const QUERY_STRING_COLUMNS: IColumn[] = [
  {
    key: "key",
    name: "Key",
    fieldName: "key",
    minWidth: 150,
    maxWidth: 250,
    isResizable: true,
  },
  {
    key: "value",
    name: "Value",
    fieldName: "value",
    minWidth: 200,
    isResizable: true,
  },
];

function TelemetryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { detail, isLoading, error } = useTelemetryDetail(id);

  if (isLoading) {
    return <ProgressIndicator label="Loading telemetry details..." />;
  }

  if (error) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Telemetry Detail"
          backPath="/telemetry/search"
          backText="Back to search"
        />
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Telemetry Detail"
        backPath="/telemetry/search"
        backText="Back to search"
      />

      {detail.containsSensitiveData && (
        <MessageBar messageBarType={MessageBarType.warning}>
          This request contained sensitive data. Some fields may be redacted.
        </MessageBar>
      )}

      <div className={styles.summary}>
        <MethodBadge method={detail.method} />
        <span className={styles.summaryPath}>
          {detail.path}
          {detail.queryString ? `?${detail.queryString}` : ""}
        </span>
        <StatusCodeBadge statusCode={detail.statusCode} />
        <span className={styles.summaryMeta}>
          HTTP/{detail.httpVersion}
        </span>
        <span className={styles.summaryMeta}>
          {detail.durationInMs.toLocaleString()} ms
        </span>
        <span className={styles.summaryMeta}>
          {new Date(detail.timestamp).toLocaleString()}
        </span>
        <span className={styles.summaryMeta}>
          Customer: {displayValue(detail.customerId)}
        </span>
        <span className={styles.summaryMeta}>
          API: {displayValue(detail.apiName)}
        </span>
        <span className={styles.summaryMeta}>
          IP: {displayValue(detail.clientIp)}
        </span>
        {detail.requestContentLengthInBytes != null && (
          <span className={styles.summaryMeta}>
            Req: {detail.requestContentLengthInBytes.toLocaleString()} bytes
          </span>
        )}
        {detail.responseContentLengthInBytes != null && (
          <span className={styles.summaryMeta}>
            Res: {detail.responseContentLengthInBytes.toLocaleString()} bytes
          </span>
        )}
        <span className={styles.summaryMeta}>
          Inserted: {new Date(detail.insertedAtUtc).toLocaleString()}
        </span>
      </div>

      {detail.queryStringEntries.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Query String Parameters</h3>
          <DetailsList
            items={detail.queryStringEntries}
            columns={QUERY_STRING_COLUMNS}
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
            compact
          />
        </div>
      )}

      <div className={styles.section}>
        <h2 className={styles.sectionHeading}>Request</h2>
        <HeadersTable
          headers={detail.requestHeaders}
          title="Request Headers"
        />
        <BodyViewer body={detail.requestBody} title="Request Body" />
        <BodyViewer
          body={detail.encryptedRequestBody}
          title="Encrypted Request Body"
        />
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionHeading}>Response</h2>
        <HeadersTable
          headers={detail.responseHeaders}
          title="Response Headers"
        />
        <BodyViewer body={detail.responseBody} title="Response Body" />
      </div>
    </div>
  );
}

export default TelemetryDetailPage;
